/**
 * fleet — derives the composer's presence from task-store rows.
 *
 * The color law (founder, 2026-08-04): anything needing attention is YELLOW;
 * else anything working is BLUE; else all-done is GREEN (recent only); an
 * empty fleet is quiet — zero pixels. Errored tasks are IGNORED outright
 * (founder 08-04 pm: stale failures "will fuck the experience") — they never
 * make a row, a color, or a whisper.
 *
 * Status ground truth: TaskStatus = 'active' | 'paused' | 'completed' | 'error'
 * (@bash/shared). "Working" means the task is actually moving — lifecycleState
 * 'processing' | 'queued', the same rule the home briefing's Working Now section
 * uses — not merely status:'active'.
 *
 * What a working row SAYS is the honest waking ladder (founder 08-19): the
 * server writes lifecycleState 'queued' for a follow-up that is durably recorded
 * but not yet driving a turn — a deferred wake, a draining relay, a sleeping
 * laptop. That is "the workspace is coming up", not "parked behind a slot", so
 * narrating it as "Queued" for the ~90 s of a cold wake made a waking task look
 * exactly like a dead one. The row now reads the same beats as the task page's
 * status line (message-list/lifecycle.ts), from the same signals and in the same
 * order — waking > starting > queued — sharing its copy via lib/starting-copy.
 * This is a second RENDERER of that truth, never a second state machine: the
 * predicates differ only because the two surfaces read different stores.
 */

import {
  DISPATCHING_LABEL,
  ENGINE_INSTALLING_LABEL,
  STARTING_LABEL,
  signInWaitLabel,
} from "@/lib/starting-copy";

export type FleetState = "needs" | "working" | "done";

export interface PendingLike {
  type?: string;
  toolUseId?: string;
  [k: string]: unknown;
}

export interface FleetSource {
  id: string;
  title?: string;
  status: string;
  lifecycleState?: string;
  pulseHeadline?: string;
  pendingInteraction?: PendingLike | null;
  updatedAt?: number;
  /**
   * This client is actively driving the task right now — the live socket phase
   * says a turn is open. Paired with lifecycleState 'queued' (no AGENT_START
   * yet) it is the row's read of the task page's `isProcessing &&
   * agentStartedAt === null`: the message is in flight, so the honest word is
   * "Starting", not "Queued".
   */
  inFlight?: boolean;
  /**
   * The box is waiting for the person to sign in (the task's sdkStatus
   * 'engine_connecting', engines T2a): the name of the plan they sign in to,
   * the task's own engine's ("Claude", "ChatGPT"). The same wait the task
   * page's line and the story's desk say.
   */
  signingIn?: string;
  /**
   * The box is installing the task's engine on its first use there (the task's
   * sdkStatus 'engine_installing'). The same line the task page and the desk say.
   */
  installing?: boolean;
}

/**
 * Account-level presence — one user, one sandbox, so a wake is not per-task.
 * `warming` is the task page's own flag (warmingFor(displayStatus, placement));
 * `placement` names the thing that is waking. Absent presence reads as "not
 * waking, cloud lane" — the pre-existing behaviour.
 */
export interface FleetPresence {
  warming?: boolean;
  placement?: "cloud" | "local";
}

export interface FleetRow {
  id: string;
  state: FleetState;
  title: string;
  sub: string;
  ago: string;
  interaction: PendingLike | null;
  /** Done inside the ambient window — only these may turn the light green. */
  recentDone?: boolean;
  /** Raw activity timestamp (ms) — the tray handle's unseen-count compares it
   *  to the last tray-open stamp (trayTick). Absent when the source had none. */
  updatedAtMs?: number;
}

/** The list keeps finished work around for a day of reference… */
const DONE_LIST_WINDOW_MS = 24 * 3_600_000;
/** …but the green LIGHT fades after the founder's ambient beat. */
const DONE_GLOW_WINDOW_MS = 30 * 60_000;
const ORD: Record<FleetState, number> = { needs: 0, working: 1, done: 2 };
const WORKING_LIFECYCLES = new Set(["processing", "queued"]);

function ago(nowMs: number, thenMs?: number): string {
  if (!thenMs) return "";
  const m = Math.max(0, Math.round((nowMs - thenMs) / 60_000));
  if (m < 1) return "now";
  if (m < 60) return `${m}m`;
  const h = Math.round(m / 60);
  if (h < 48) return `${h}h`;
  return `${Math.round(h / 24)}d`;
}

function stateOf(tk: FleetSource): FleetState | null {
  if (tk.status === "error") return null; // errored tasks are ignored outright
  if (tk.pendingInteraction) return "needs";
  if (WORKING_LIFECYCLES.has(tk.lifecycleState ?? "")) return "working";
  if (tk.status === "completed") return "done";
  return null; // idle-active / paused tasks carry no presence
}

/**
 * What a working row says. Priority mirrors deriveLifecycle's boot ladder
 * exactly: waking > starting > (nothing to report) — and none of it applies once
 * the agent has really started.
 */
function workingSub(tk: FleetSource, presence: FleetPresence): string {
  // lifecycleState 'processing' IS the server's AGENT_START: the turn is open
  // and the agent is moving, so the live narration is the truth. Account-level
  // warming can never caption this row — a wake and an open turn cannot coexist
  // (presence only reports 'warming' while no relay is alive).
  if (tk.lifecycleState !== "queued")
    return tk.signingIn
      ? signInWaitLabel(tk.signingIn)
      : tk.installing
        ? ENGINE_INSTALLING_LABEL
        : tk.pulseHeadline || "Working in the background";

  // The turn has NOT opened. A pulse headline here is last turn's narration, so
  // it is the very lie this ladder replaces — say what is happening instead.
  // The sign-in sits between the two, as on the task page: the computer is up
  // (no sign-in opens on a sleeping one) and the wait is now the person's. The
  // engine's install sits with it: only a computer that is up installs anything.
  if (presence.warming) return STARTING_LABEL[presence.placement ?? "cloud"];
  if (tk.signingIn) return signInWaitLabel(tk.signingIn);
  if (tk.installing) return ENGINE_INSTALLING_LABEL;
  if (tk.inFlight) return DISPATCHING_LABEL;
  // Durably recorded and nothing is driving it: genuinely parked.
  return "Queued";
}

function subOf(
  state: FleetState,
  tk: FleetSource,
  presence: FleetPresence
): string {
  switch (state) {
    case "needs":
      return "Waiting on you";
    case "working":
      return workingSub(tk, presence);
    case "done":
      return "Finished, on your desk";
  }
}

export function deriveFleet(
  tasks: FleetSource[],
  nowMs: number,
  presence: FleetPresence = {}
): FleetRow[] {
  return tasks
    .map((tk) => ({ tk, state: stateOf(tk) }))
    .filter(
      (x): x is { tk: FleetSource; state: FleetState } => x.state !== null
    )
    .filter(
      (x) =>
        x.state !== "done" ||
        x.tk.updatedAt == null ||
        nowMs - x.tk.updatedAt <= DONE_LIST_WINDOW_MS
    )
    .map(({ tk, state }) => ({
      id: tk.id,
      state,
      title: tk.title || "Untitled task",
      sub: subOf(state, tk, presence),
      ago: ago(nowMs, tk.updatedAt),
      interaction: (state === "needs" && tk.pendingInteraction) || null,
      ...(tk.updatedAt != null ? { updatedAtMs: tk.updatedAt } : {}),
      recentDone:
        state === "done" &&
        tk.updatedAt != null &&
        nowMs - tk.updatedAt <= DONE_GLOW_WINDOW_MS,
    }))
    .sort((a, b) => ORD[a.state] - ORD[b.state]);
}

export function deriveUrgency(
  rows: FleetRow[]
): "needs" | "work" | "done" | "quiet" {
  if (rows.some((r) => r.state === "needs")) return "needs";
  if (rows.some((r) => r.state === "working")) return "work";
  return rows.some((r) => r.recentDone) ? "done" : "quiet";
}
